import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, ShieldCheck, Pencil, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";
import { DEPARTMENTS, SUB_DEPARTMENTS, nextEmployeeId } from "@/lib/departments";

const ALL_ROLES = ["ADMIN", "CREATOR", "REVIEWER", "FINAL_APPROVER"];
const ROLE_LABEL = { ADMIN: "Admin", CREATOR: "Creator", REVIEWER: "Reviewer", FINAL_APPROVER: "Final Approver" };

const empty = { full_name: "", email: "", roles: ["CREATOR"], status: "active", reporting_to: "", designation: "", department: "", sub_department: "", employee_id: "", module_access: [], wfh_allowed: false, birthday: "", date_of_joining: "", dept_scope: "user" };

// Permission classification: Administrator (ADMIN role) > Department Manager
// (explicit dept_scope=manager OR anyone with direct reports) > Department User.
export function permissionLabel(u, allUsers) {
  if ((u.roles || []).includes("ADMIN")) return "Intranet Administrator";
  if (u.dept_scope === "manager" || (allUsers || []).some((x) => x.reporting_to === u.user_id)) return "Department Manager";
  return "Department User";
}

function DepartmentCard({ dept, onSaved }) {
  const [draft, setDraft] = useState(dept);
  useEffect(() => setDraft(dept), [dept]);
  const save = async () => {
    try {
      await api.put(`/departments/${dept.dept_id}`, { description: draft.description, active: draft.active, modules: draft.modules });
      toast.success(`${dept.name} updated`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update department"); }
  };
  return (
    <div className={`rounded-xl border p-4 ${draft.active ? "border-border" : "border-dashed border-border opacity-70"}`} data-testid={`dept-${dept.dept_id}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="font-display font-bold text-sm">{dept.name}</p>
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Checkbox data-testid={`dept-active-${dept.dept_id}`} checked={draft.active} onCheckedChange={(v) => setDraft({ ...draft, active: !!v })} /> Active
        </label>
      </div>
      <Input data-testid={`dept-desc-${dept.dept_id}`} value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="mt-2 h-8 text-xs" placeholder="Description" />
      <div className="mt-3 space-y-1.5">
        {(draft.modules || []).map((m, i) => (
          <label key={m.name} className="flex items-center gap-2 text-xs">
            <Checkbox data-testid={`dept-${dept.dept_id}-mod-${i}`} checked={!!m.active}
              onCheckedChange={(v) => setDraft({ ...draft, modules: draft.modules.map((x, xi) => (xi === i ? { ...x, active: !!v } : x)) })} />
            <span className="font-medium">{m.name}</span>
            {!m.active && <span className="text-[10px] text-muted-foreground">(coming soon)</span>}
          </label>
        ))}
        {(draft.modules || []).length === 0 && <p className="text-[11px] text-muted-foreground">No modules yet.</p>}
      </div>
      <Button size="sm" variant="outline" data-testid={`dept-save-${dept.dept_id}`} className="mt-3 h-7 text-xs" onClick={save}>Save</Button>
    </div>
  );
}

export default function AccessControl() {
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [modules, setModules] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [toDelete, setToDelete] = useState(null);

  const load = () => api.get("/access/users").then((r) => setUsers(r.data));
  const loadDepartments = () => api.get("/departments").then((r) => {
    setDepartments(r.data || []);
    setModules((r.data || []).flatMap((d) => (d.modules || []).map((m) => ({ name: m.name, dept: d.name, active: m.active }))));
  }).catch(() => {});
  useEffect(() => { load(); loadDepartments(); }, []);

  const summary = {
    total: users.length,
    active: users.filter((u) => u.status === "active").length,
    inactive: users.filter((u) => u.status !== "active").length,
    admins: users.filter((u) => (u.roles || []).includes("ADMIN")).length,
  };

  const nameOf = (id) => users.find((u) => u.user_id === id)?.name || null;

  const openNew = () => { setEditing(null); setForm({ ...empty, employee_id: nextEmployeeId(users) }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ full_name: u.name, email: u.email, roles: u.roles || [], status: u.status || "active", reporting_to: u.reporting_to || "", designation: u.designation || "", department: u.department || "", sub_department: u.sub_department || "", employee_id: u.employee_id || "", module_access: u.module_access || [], wfh_allowed: !!u.wfh_allowed, birthday: u.birthday ? "2000-" + u.birthday.slice(-5) : "", date_of_joining: u.date_of_joining || "", dept_scope: u.dept_scope || "user" }); setOpen(true); };

  const toggleRole = (r) => setForm((f) => ({ ...f, roles: f.roles.includes(r) ? f.roles.filter((x) => x !== r) : [...f.roles, r] }));
  const toggleModule = (m) => setForm((f) => ({ ...f, module_access: f.module_access.includes(m) ? f.module_access.filter((x) => x !== m) : [...f.module_access, m] }));

  const save = async () => {
    if (!form.full_name.trim() || !form.email.trim()) return toast.error("Name and email are required");
    try {
      if (editing) {
        await api.patch(`/access/users/${editing.user_id}`, { full_name: form.full_name, email: form.email, roles: form.roles, status: form.status, reporting_to: form.reporting_to || "", designation: form.designation, department: form.department, sub_department: form.sub_department, employee_id: form.employee_id, module_access: form.module_access, wfh_allowed: form.wfh_allowed, birthday: form.birthday, date_of_joining: form.date_of_joining, dept_scope: form.dept_scope });
        toast.success("User updated");
      } else {
        await api.post("/access/users", { ...form, reporting_to: form.reporting_to || null });
        toast.success("User added");
      }
      setOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save user");
    }
  };

  const toggleStatus = async (u) => {
    try {
      await api.patch(`/access/users/${u.user_id}`, { status: u.status === "active" ? "inactive" : "active" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not change status"); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/access/users/${toDelete.user_id}`);
      toast.success("User deleted");
      setToDelete(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete user");
      setToDelete(null);
    }
  };

  const Stat = ({ label, value, testid }) => (
    <div data-testid={testid} className="rounded-xl border border-border bg-card px-4 py-3">
      <p className="text-2xl font-bold font-display">{value}</p>
      <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
    </div>
  );

  return (
    <div className="fade-up space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <p className="text-muted-foreground text-sm max-w-2xl">The central source of truth for who has access, who is active, and what each person can do. Roles drive permissions across every module.</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-button" onClick={openNew} className="gap-1.5"><UserPlus className="h-4 w-4" /> Add User</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[88vh] overflow-y-auto" aria-describedby={undefined}>
            <DialogHeader><DialogTitle className="font-display">{editing ? "Edit user" : "Add user"}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              {/* Active / Inactive stays separately at the top */}
              <div className="flex items-center justify-between rounded-lg border border-border p-3">
                <span className="text-sm font-medium">Active</span>
                <Switch data-testid="user-status-switch" disabled={editing?.protected} checked={form.status === "active"} onCheckedChange={(v) => setForm({ ...form, status: v ? "active" : "inactive" })} />
              </div>

              <div><Label>Full Name</Label>
                <Input data-testid="user-name-input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="mt-1" /></div>
              <div><Label>Email ID</Label>
                <Input data-testid="user-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" placeholder="name@livelife.com" />
                {editing?.protected && <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1"><Lock className="h-3 w-3" /> Master administrator email is fixed.</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Date of Birth <span className="text-muted-foreground font-normal">(optional)</span></Label>
                  <Input data-testid="user-birthday-input" type="date" value={form.birthday} onChange={(e) => setForm({ ...form, birthday: e.target.value })} className="mt-1" />
                  <p className="text-[11px] text-muted-foreground mt-1">Only day & month are used — for automatic birthday wishes. The year is never shown.</p>
                </div>
                <div><Label>Date of Joining <span className="text-muted-foreground font-normal">(optional)</span></Label>
                  <Input data-testid="user-joining-input" type="date" value={form.date_of_joining} onChange={(e) => setForm({ ...form, date_of_joining: e.target.value })} className="mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Employee ID</Label>
                  <Input data-testid="user-employeeid-input" value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} className="mt-1" placeholder="e.g. LLE-042" />
                  <p className="text-[11px] text-muted-foreground mt-1">Unique. Does not change the internal User ID or history.</p>
                </div>
                <div><Label>Designation</Label>
                  <Input data-testid="user-designation-input" value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} className="mt-1" placeholder="e.g. Video Editor" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Department</Label>
                  <Select value={form.department || "none"} onValueChange={(v) => setForm({ ...form, department: v === "none" ? "" : v, sub_department: "" })}>
                    <SelectTrigger data-testid="user-department-input" className="mt-1"><SelectValue placeholder="Select department" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Sub-Department <span className="text-muted-foreground font-normal">(optional)</span></Label>
                  <Select value={form.sub_department || "none"} onValueChange={(v) => setForm({ ...form, sub_department: v === "none" ? "" : v })} disabled={!form.department}>
                    <SelectTrigger data-testid="user-subdepartment-input" className="mt-1"><SelectValue placeholder={form.department ? "Select sub-department" : "Select a department first"} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {(SUB_DEPARTMENTS[form.department] || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Reporting Person</Label>
                <Select value={form.reporting_to || "none"} onValueChange={(v) => setForm({ ...form, reporting_to: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="reporting-person-select" className="mt-1"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {users.filter((u) => u.status === "active" && u.user_id !== editing?.user_id).map((u) => (
                      <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1">A management relationship — the Reporting Person can view this member's work, tracker and leave. It is not an Admin role.</p>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-border p-3">
                <span className="text-sm font-medium">Allow Work From Home <span className="block text-[11px] font-normal text-muted-foreground">Employee can select "Work From Home" as work location in Daily Tracker</span></span>
                <Switch data-testid="user-wfh-switch" checked={!!form.wfh_allowed} onCheckedChange={(v) => setForm({ ...form, wfh_allowed: v })} />
              </div>

              <div>
                <Label>Permission</Label>
                <RadioGroup value={form.dept_scope} onValueChange={(v) => setForm({ ...form, dept_scope: v })} className="mt-2 space-y-2">
                  <label className="flex items-start gap-2.5 rounded-lg border border-border p-3 cursor-pointer hover:bg-accent">
                    <RadioGroupItem data-testid="perm-dept-user" value="user" className="mt-0.5" />
                    <span>
                      <span className="text-sm font-medium block">Department User</span>
                      <span className="text-[11px] text-muted-foreground block mt-0.5">Own work/information and authorised department tabs. Home is automatically available to everyone.</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2.5 rounded-lg border border-border p-3 cursor-pointer hover:bg-accent">
                    <RadioGroupItem data-testid="perm-dept-manager" value="manager" className="mt-0.5" />
                    <span>
                      <span className="text-sm font-medium block">Department Manager</span>
                      <span className="text-[11px] text-muted-foreground block mt-0.5">Department User + authorised team visibility for department employees — attendance, Daily Tracker, leave and relevant department work.</span>
                    </span>
                  </label>
                </RadioGroup>
                <p className="text-[11px] text-muted-foreground mt-1.5">Create, Review, Approve, Schedule and Publish stay separate — grant them individually via Roles below.</p>
              </div>

              <div>
                <Label>Roles <span className="text-muted-foreground font-normal">(granular module permissions)</span></Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {ALL_ROLES.map((r) => (
                    <label key={r} className="flex items-center gap-2 rounded-lg border border-border p-2.5 cursor-pointer hover:bg-accent">
                      <Checkbox data-testid={`role-${r}`} checked={form.roles.includes(r)} onCheckedChange={() => toggleRole(r)} />
                      <span className="text-sm font-medium">{ROLE_LABEL[r]}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label>Module Access <span className="text-muted-foreground font-normal">(foundation — future departments appear here)</span></Label>
                <div className="grid grid-cols-2 gap-2 mt-2 max-h-40 overflow-y-auto">
                  {modules.map((m) => (
                    <label key={m.name} className="flex items-center gap-2 rounded-lg border border-border p-2.5 cursor-pointer hover:bg-accent">
                      <Checkbox data-testid={`module-${m.name.replace(/\s+/g, "-")}`} checked={form.module_access.includes(m.name)} onCheckedChange={() => toggleModule(m.name)} />
                      <span className="text-xs font-medium">{m.name}{!m.active && <span className="text-muted-foreground"> (soon)</span>}</span>
                    </label>
                  ))}
                  {modules.length === 0 && <p className="text-xs text-muted-foreground col-span-2">Loading modules…</p>}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="save-user-button" onClick={save}>{editing ? "Save changes" : "Add user"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total Users" value={summary.total} testid="stat-total-users" />
        <Stat label="Active" value={summary.active} testid="stat-active-users" />
        <Stat label="Inactive" value={summary.inactive} testid="stat-inactive-users" />
        <Stat label="Admins" value={summary.admins} testid="stat-admin-users" />
      </div>

      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
              <th className="text-left font-semibold px-4 py-3">User ID</th>
              <th className="text-left font-semibold px-4 py-3">Employee ID</th>
              <th className="text-left font-semibold px-4 py-3">Name</th>
              <th className="text-left font-semibold px-4 py-3">Permission</th>
              <th className="text-left font-semibold px-4 py-3">Email</th>
              <th className="text-left font-semibold px-4 py-3">Roles</th>
              <th className="text-left font-semibold px-4 py-3">Reports To</th>
              <th className="text-left font-semibold px-4 py-3">Status</th>
              <th className="text-right font-semibold px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} data-testid={`access-row-${u.user_id}`} className="border-b border-border last:border-0">
                <td className="px-4 py-3 font-mono text-primary">{u.user_code || "—"}</td>
                <td className="px-4 py-3 font-mono" data-testid={`employee-id-${u.user_id}`}>{u.employee_id || "—"}</td>
                <td className="px-4 py-3 font-medium">
                  <span className="flex items-center gap-1.5">{u.name}{u.protected && <Lock className="h-3 w-3 text-muted-foreground" />}</span>
                </td>
                <td className="px-4 py-3" data-testid={`user-type-${u.user_id}`}>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${permissionLabel(u, users) === "Intranet Administrator" ? "bg-primary/10 text-primary" : permissionLabel(u, users) === "Department Manager" ? "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300" : "bg-muted text-muted-foreground"}`}>
                    {permissionLabel(u, users)}
                  </span>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {(u.roles || []).map((r) => (
                      <span key={r} className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[11px] font-semibold">
                        {r === "ADMIN" && <ShieldCheck className="h-3 w-3" />}{ROLE_LABEL[r] || r}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3 text-muted-foreground" data-testid={`reports-to-${u.user_id}`}>{u.reporting_to ? nameOf(u.reporting_to) || "—" : "—"}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${u.status === "active" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300" : "bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300"}`}>
                    {u.status === "active" ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <button data-testid={`edit-user-${u.user_id}`} onClick={() => openEdit(u)} className="p-1.5 rounded-md hover:bg-accent text-muted-foreground"><Pencil className="h-4 w-4" /></button>
                    {!u.protected && (
                      <Button data-testid={`toggle-status-${u.user_id}`} onClick={() => toggleStatus(u)} size="sm" variant="outline" className="h-7 text-xs">
                        {u.status === "active" ? "Deactivate" : "Activate"}
                      </Button>
                    )}
                    {u.protected ? (
                      <span className="text-[11px] text-muted-foreground">Protected</span>
                    ) : u.status !== "active" ? (
                      <button data-testid={`delete-user-${u.user_id}`} onClick={() => setToDelete(u)} className="p-1.5 rounded-md hover:bg-rose-500/10 text-muted-foreground hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Departments & Modules — future department architecture (foundation) */}
      <div className="mt-10">
        <h2 className="font-display text-lg font-bold flex items-center gap-2">Departments &amp; Modules</h2>
        <p className="text-sm text-muted-foreground mt-1">The company-wide Intranet architecture. Detailed workflows for future departments will be built one at a time — this is the structural foundation (Department · Module · Active/Inactive · Access).</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4" data-testid="departments-grid">
          {departments.map((d) => <DepartmentCard key={d.dept_id} dept={d} onSaved={loadDepartments} />)}
        </div>
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this user permanently?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm space-y-1">
                <p>User ID: <span className="font-mono">{toDelete?.user_code}</span></p>
                <p>Name: {toDelete?.name}</p>
                <p>Email: {toDelete?.email}</p>
                <p className="text-rose-600 font-medium">This action cannot be undone.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-button">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-button" onClick={doDelete} className="bg-rose-600 hover:bg-rose-700">Delete User</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
